import { ChangeDetectionStrategy, Component, computed, inject, input, Signal } from '@angular/core';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { PermissionsEnum } from 'mbs-ui-shared@models';
import AdministratorInCamelCase from 'mbs-ui-shared@models/AdministratorInCamelCase';
import { CompanyDestination } from 'mbs-ui-shared@models/Company';
import { StorageTypeIconPipe } from 'mbs-ui-shared@utils/pipes/storage-type-icon.pipe';
import { CompanyWizardFacade } from 'wizards/services/entity-wizards/company/company-wizard.facade';
import { CompanyBackupTemplate } from '../../models/company-wizard-models';
import { TextEllipsisComponent } from 'mbs-ui-kit';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [StorageTypeIconPipe, TextEllipsisComponent],
  selector: 'mbs-company-summary-step',
  standalone: true,
  templateUrl: './company-summary-step.component.html'
})
export class CompanySummaryStepComponent {
  protected readonly ability = inject(AbilityService);
  private readonly companyWizardFacade = inject(CompanyWizardFacade);
  private readonly i18nPipe = inject(I18NextPipe);

  public readonly administrators = input<AdministratorInCamelCase[]>([]);
  public readonly templates = input<CompanyBackupTemplate[]>([]);

  public readonly PermissionsEnum = PermissionsEnum;
  public readonly elementSelectors = {
    id: {
      administrators: 'company-summary-administrators',
      destinations: 'company-summary-destinations',
      templates: 'company-summary-templates'
    }
  };

  public readonly labels = {
    administrators: this.getText('administrators'),
    companyName: this.getText('companyName'),
    destinations: this.getText('destinations'),
    noAdministrators: this.getText('noAdministrators'),
    noDestinations: this.getText('noDestinations'),
    noTemplates: this.getText('noTemplates'),
    templates: this.getText('templates'),
    title: this.getText('title')
  };

  public readonly companyName = computed(() => this.companyWizardFacade.company()?.name);

  public readonly destinations: Signal<CompanyDestination[]> = computed(
    () => this.companyWizardFacade.company()?.inheritedDestinations ?? []
  );

  public readonly companyAdministrators = computed(() =>
    this.administrators()
      .filter((administrator) => !AdministratorInCamelCase.canManageAllCompanies(administrator))
      .map((administrator) => [administrator.firstName, administrator.lastName].filter(Boolean).join(' ') || administrator.email)
  );

  public readonly allCompaniesAdministratorsCount = computed(
    () => this.administrators().filter((administrator) => AdministratorInCamelCase.canManageAllCompanies(administrator)).length
  );

  public readonly allCompaniesAdministrators = computed(() =>
    this.getText('allCompaniesAdministrators', { count: this.allCompaniesAdministratorsCount() })
  );

  public readonly selectedTemplates = computed(() => this.templates().filter(({ create }) => create));

  // Private section
  private getText(key: string, options?: Record<string, unknown>): string {
    return this.i18nPipe.transform(`wizards:entityWizards.company.summaryStep.${key}`, options);
  }
}
